// src/components/cooler/inventory/components/ItemDetails/ViewDetails.tsx

import React, { useState } from 'react';
import {
  Stack,
  Grid,
  Paper,
  Typography,
  Chip,
  Box,
  IconButton,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Fade,
} from '@mui/material';
import {
  Tag as CategoryIcon,
  MapPin as LocationIcon,
  Euro,
  Power,
  AlertTriangle,
  CheckCircle,
  Clock,
  Edit as EditIcon,
  Check as SaveIcon,
  X as CancelIcon,
  Building as BusinessIcon,
  Info as InfoIcon,
} from 'lucide-react';

import type { InventoryItem } from '../../../types/inventoryTypes';
import { formatCurrency } from './utils';
import { CATEGORIES, VENDORS } from '../../../constants/inventoryConstants';

interface ViewDetailsProps {
  item: InventoryItem;
  onUpdate?: (field: string, value: any) => void;
}

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; action?: React.ReactNode }> = ({ 
  icon, 
  title,
  action 
}) => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 1, 
    mb: 2, 
    p: 1,
    borderRadius: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.02)'
  }}>
    {icon}
    <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
      {title}
    </Typography>
    {action}
  </Box>
);

const ViewDetails: React.FC<ViewDetailsProps> = ({ item, onUpdate }) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<any>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const startEdit = (field: string, currentValue: any) => {
    console.log('🔍 DEBUG startEdit:', { field, currentValue, type: typeof currentValue });
    setEditingField(field);
    
    // Fix null/undefined values for date fields
    if ((field.includes('Date') || field === 'purchaseDate') && !currentValue) {
      setEditValue(''); // Empty string for date inputs
    } else {
      setEditValue(currentValue || '');
    }
  };

  const saveEdit = () => {
    if (editingField && onUpdate) {
      onUpdate(editingField, editValue);
    }
    setEditingField(null);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const getMaintenanceStatusChip = () => {
    const config = {
      'ok': { color: 'success' as const, icon: <CheckCircle size={16} /> },
      'due': { color: 'warning' as const, icon: <Clock size={16} /> },
      'overdue': { color: 'error' as const, icon: <AlertTriangle size={16} /> }
    };

    const status = item.maintenanceStatus;
    if (!status || !config[status]) return null;

    return (
      <Chip
        size="small"
        icon={config[status].icon}
        label={status.toUpperCase()}
        color={config[status].color}
      />
    );
  };

  const EditableField = ({ 
    field, 
    value, 
    displayValue, 
    type = 'text',
    options = [],
    multiline = false 
  }: {
    field: string;
    value: any;
    displayValue?: string;
    type?: 'text' | 'select' | 'autocomplete' | 'tags';
    options?: readonly string[];
    multiline?: boolean;
  }) => {
    const isEditing = editingField === field;

    if (isEditing) {
      return (
        <Fade in={true}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: multiline || isMobile ? 'column' : 'row', 
            alignItems: multiline || isMobile ? 'stretch' : 'center', 
            gap: 1, 
            width: '100%',
            p: 1,
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
            borderRadius: 1
          }}>
            {type === 'select' ? (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                >
                  {options.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : type === 'autocomplete' ? (
              <Autocomplete
                size="small"
                freeSolo
                options={options}
                value={editValue}
                onChange={(_, newValue) => setEditValue(newValue)}
                onInputChange={(_, newValue) => setEditValue(newValue)}
                renderInput={(params) => <TextField {...params} autoFocus />}
                sx={{ minWidth: isMobile ? '100%' : 200 }}
              />
            ) : type === 'tags' ? (
              <TextField
                size="small"
                value={Array.isArray(editValue) ? editValue.join(', ') : editValue}
                onChange={(e) => setEditValue(e.target.value.split(',').map(tag => tag.trim()))}
                placeholder="Separate tags with commas"
                autoFocus
                multiline={multiline}
                fullWidth={isMobile}
                sx={{ minWidth: isMobile ? '100%' : 200 }}
              />
            ) : (
              <TextField
                size="small"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                multiline={multiline}
                rows={multiline ? 3 : 1}
                fullWidth={isMobile}
                sx={{ 
                  minWidth: multiline || isMobile ? '100%' : 200,
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            )}
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              justifyContent: isMobile ? 'flex-end' : 'flex-start'
            }}>
              <IconButton size="small" onClick={saveEdit} color="primary">
                <SaveIcon size={16} />
              </IconButton>
              <IconButton size="small" onClick={cancelEdit}>
                <CancelIcon size={16} />
              </IconButton>
            </Box>
          </Box>
        </Fade>
      );
    }

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: 1, 
        minHeight: 32, 
        width: '100%',
        '&:hover': onUpdate ? {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 1
        } : {}
      }}>
        <Box sx={{ flexGrow: 1, p: 0.5 }}>
          <Typography variant="body1" sx={{ whiteSpace: multiline ? 'pre-wrap' : 'normal' }}>
            {displayValue || value || 'N/A'}
          </Typography>
        </Box>
        {onUpdate && (
          <IconButton 
            size="small" 
            onClick={() => startEdit(field, value)}
            sx={{ 
              opacity: 0.7, 
              '&:hover': { opacity: 1, backgroundColor: 'rgba(25, 118, 210, 0.04)' }
            }}
          >
            <EditIcon size={16} />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Basic Information Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: isMobile ? 2 : 3,
            height: '100%',
            backgroundColor: 'rgba(25, 118, 210, 0.02)'
          }}>
            <SectionHeader 
              icon={<CategoryIcon size={20} />} 
              title="Basic Information"
            />
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Category
                </Typography>
                {editingField === 'category' ? (
                  <EditableField 
                    field="category" 
                    value={item.category} 
                    type="select"
                    options={CATEGORIES}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={<CategoryIcon size={16} />}
                      label={item.category}
                      size="small"
                    />
                    {onUpdate && (
                      <IconButton 
                        size="small" 
                        onClick={() => startEdit('category', item.category)}
                        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                      >
                        <EditIcon size={16} />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <EditableField 
                  field="description" 
                  value={item.description}
                  displayValue={item.description || 'No description provided'}
                  multiline
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Location Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: isMobile ? 2 : 3,
            height: '100%',
            backgroundColor: 'rgba(76, 175, 80, 0.02)'
          }}>
            <SectionHeader 
              icon={<LocationIcon size={20} />} 
              title="Location Information"
            />
            
            <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<LocationIcon size={16} />}
                label={item.primaryLocation}
                size="small"
              />
              {item.secondLocation && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={item.secondLocation}
                  sx={{ borderStyle: 'dashed' }}
                />
              )}
              {item.secondaryLocation && item.secondaryLocation !== 'N/A' && (
                <Chip
                  icon={<LocationIcon size={16} />}
                  label={`Alt: ${item.secondaryLocation}`}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Status Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: isMobile ? 2 : 3,
            height: '100%',
            backgroundColor: 'rgba(156, 39, 176, 0.02)'
          }}>
            <SectionHeader 
              icon={<InfoIcon size={20} />} 
              title="Status & Technical"
            />
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Status
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {getMaintenanceStatusChip()}
                  {item.isInsured ? (
                    <Chip
                      size="small"
                      icon={<Euro size={16} />}
                      label="Insured"
                      color="success"
                    />
                  ) : (item.purchaseValue || 0) >= 500 ? (
                    <Chip
                      size="small"
                      icon={<AlertTriangle size={16} />}
                      label="Insurance Required"
                      color="error"
                    />
                  ) : null}
                  {item.requiresPower && (
                    <Chip
                      size="small"
                      icon={<Power size={16} />}
                      label={`Power: ${item.powerLocation || 'Required'}`}
                      color="warning"
                    />
                  )}
                </Stack>
              </Box>

              {item.serialNumber && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Serial Number
                  </Typography>
                  <EditableField 
                    field="serialNumber" 
                    value={item.serialNumber}
                  />
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Purchase Information Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: isMobile ? 2 : 3,
            height: '100%',
            backgroundColor: 'rgba(255, 152, 0, 0.02)'
          }}>
            <SectionHeader 
              icon={<BusinessIcon size={20} />} 
              title="Purchase Information"
            />
            
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Purchase Date
                </Typography>
                <EditableField 
                  field="purchaseDate" 
                  value={item.purchaseDate}
                  displayValue={item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString() : 'N/A'}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Vendor
                </Typography>
                <EditableField 
                  field="vendor" 
                  value={item.vendor}
                  type="autocomplete"
                  options={VENDORS}
                />
              </Box>
              {(item.purchaseValue || onUpdate) && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Purchase Value
                  </Typography>
                  <EditableField 
                    field="purchaseValue" 
                    value={item.purchaseValue}
                    displayValue={item.purchaseValue ? formatCurrency(item.purchaseValue) : 'N/A'}
                  />
                </Box>
              )}
              {(item.currentValue || onUpdate) && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Value
                  </Typography>
                  <EditableField 
                    field="currentValue" 
                    value={item.currentValue}
                    displayValue={item.currentValue ? formatCurrency(item.currentValue) : 'N/A'}
                  />
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Notes Section */}
        {(item.notes || onUpdate) && (
          <Grid item xs={12}>
            <Paper sx={{ 
              p: isMobile ? 2 : 3,
              backgroundColor: 'rgba(96, 125, 139, 0.02)'
            }}>
              <SectionHeader 
                icon={<InfoIcon size={20} />} 
                title="Notes"
              />
              <EditableField 
                field="notes" 
                value={item.notes}
                displayValue={item.notes || 'No notes added'}
                multiline
              />
            </Paper>
          </Grid>
        )}

        {/* Tags and Additional Info */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: isMobile ? 2 : 3,
            backgroundColor: 'rgba(121, 85, 72, 0.02)'
          }}>
            <SectionHeader 
              icon={<CategoryIcon size={20} />} 
              title="Tags & Additional Information"
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Tags
                </Typography>
                {editingField === 'tags' ? (
                  <EditableField 
                    field="tags" 
                    value={item.tags}
                    type="tags"
                  />
                ) : (
                  <Box>
                    {item.tags && item.tags.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {item.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {onUpdate && (
                          <IconButton 
                            size="small" 
                            onClick={() => startEdit('tags', item.tags)}
                            sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                          >
                            <EditIcon size={16} />
                          </IconButton>
                        )}
                      </Stack>
                    ) : (
                      <EditableField 
                        field="tags" 
                        value={item.tags}
                        displayValue="No tags"
                        type="tags"
                      />
                    )}
                  </Box>
                )}
              </Grid>

              {item.quantity > 1 && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Quantity
                  </Typography>
                  <EditableField 
                    field="quantity" 
                    value={item.quantity}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewDetails;